import React, { useMemo, useState } from "react";

const PILLAR_LABELS = {
  "problem-reveal": "Problem→Reveal",
  "feature-spotlight": "Feature",
  "club-spotlight": "Club",
  "comparison": "Comparison",
  "how-to": "How-to",
};

const PILLAR_TINT = {
  "problem-reveal": "#787f9a",
  "feature-spotlight": "#3a6a4c",
  "club-spotlight": "#f59e0b",
  "comparison": "#c46b4e",
  "how-to": "#9ea5bf",
};

const STATUS_LABELS = {
  draft: "Draft",
  ready_to_render: "Ready",
  rendered: "Rendered",
  published: "Published",
  archived: "Archived",
};

// Display order for status groups — drafts first (most actionable), archived last.
const STATUS_GROUP_ORDER = ["draft", "ready_to_render", "rendered", "published", "archived"];

const PLATFORM_GLYPH = {
  instagram: "IG",
  facebook: "FB",
  tiktok: "TT",
};

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

function firstRenderUrl(brief) {
  const renders = brief?.renders;
  if (!Array.isArray(renders) || renders.length === 0) return null;
  const sorted = [...renders].sort((a, b) => (a.slide_index || 0) - (b.slide_index || 0));
  return sorted[0]?.url || null;
}

function BriefRow({ brief, isActive, onSelect }) {
  const status = brief.status || "draft";
  const thumb = firstRenderUrl(brief);
  const tint = PILLAR_TINT[brief.pillar] || "#787f9a";
  const renderCount = Array.isArray(brief.renders) ? brief.renders.length : 0;
  const pubs = Array.isArray(brief.publications) ? brief.publications : [];
  const postedTo = pubs.map((p) => `@${p.account_username || "?"}`).join(", ");
  return (
    <li
      className={`content-hub__brief-row ${isActive ? "is-active" : ""}`}
      onClick={() => onSelect(brief.id)}
    >
      <div className="content-hub__brief-thumb" style={{ background: thumb ? "transparent" : tint }}>
        {thumb ? (
          <img src={thumb} alt="" loading="lazy" />
        ) : (
          <span className="content-hub__brief-thumb-glyph">
            {PLATFORM_GLYPH[brief.platform] || "•"}
          </span>
        )}
      </div>
      <div className="content-hub__brief-row-body">
        <div className="content-hub__brief-row-top">
          <span className="content-hub__brief-topic">{brief.topic}</span>
        </div>
        <div className="content-hub__brief-row-meta">
          <span className="content-hub__tag">{PILLAR_LABELS[brief.pillar] || brief.pillar}</span>
          <span className="content-hub__tag">{brief.platform || "—"}</span>
          {renderCount > 0 && (
            <span className="content-hub__tag content-hub__tag--soft">{renderCount} render{renderCount === 1 ? "" : "s"}</span>
          )}
          {pubs.length > 0 && (
            <span className="content-hub__tag content-hub__tag--posted" title={`Posted to ${postedTo}`}>
              ● Posted{pubs.length > 1 ? ` ×${pubs.length}` : ""}
            </span>
          )}
          <span className="content-hub__brief-date">{formatDate(brief.updated_at || brief.created_at)}</span>
        </div>
      </div>
    </li>
  );
}

export default function BriefList({ briefs, selectedId, onSelect }) {
  const [query, setQuery] = useState("");
  const [collapsed, setCollapsed] = useState({});

  const filtered = useMemo(() => {
    if (!query.trim()) return briefs;
    const q = query.trim().toLowerCase();
    return briefs.filter((b) => {
      const haystack = `${b.topic || ""} ${b.pillar || ""} ${b.platform || ""}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [briefs, query]);

  const grouped = useMemo(() => {
    const byStatus = new Map();
    for (const status of STATUS_GROUP_ORDER) byStatus.set(status, []);
    for (const b of filtered) {
      const s = b.status || "draft";
      if (!byStatus.has(s)) byStatus.set(s, []);
      byStatus.get(s).push(b);
    }
    for (const list of byStatus.values()) {
      list.sort((a, b) => {
        const ta = new Date(a.updated_at || a.created_at || 0).getTime();
        const tb = new Date(b.updated_at || b.created_at || 0).getTime();
        return tb - ta;
      });
    }
    return byStatus;
  }, [filtered]);

  if (!briefs || briefs.length === 0) {
    return (
      <div className="content-hub__empty">
        <p>No briefs yet. Create one to start.</p>
      </div>
    );
  }

  const toggle = (status) => setCollapsed((c) => ({ ...c, [status]: !c[status] }));

  return (
    <div className="content-hub__brief-list-wrap">
      <div className="content-hub__brief-search">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search briefs…"
          aria-label="Search briefs"
        />
      </div>
      {STATUS_GROUP_ORDER.map((status) => {
        const list = grouped.get(status) || [];
        if (list.length === 0) return null;
        const isCollapsed = !!collapsed[status];
        return (
          <section key={status} className={`content-hub__brief-group content-hub__brief-group--${status}`}>
            <button
              type="button"
              className="content-hub__brief-group-head"
              onClick={() => toggle(status)}
              aria-expanded={!isCollapsed}
            >
              <span className="content-hub__brief-group-chev" data-collapsed={isCollapsed}>▾</span>
              <span className="content-hub__brief-group-label">{STATUS_LABELS[status] || status}</span>
              <span className="content-hub__brief-group-count">{list.length}</span>
            </button>
            {!isCollapsed && (
              <ul className="content-hub__brief-list">
                {list.map((b) => (
                  <BriefRow
                    key={b.id}
                    brief={b}
                    isActive={b.id === selectedId}
                    onSelect={onSelect}
                  />
                ))}
              </ul>
            )}
          </section>
        );
      })}
      {filtered.length === 0 && (
        <div className="content-hub__empty">
          <p>No briefs match "{query}".</p>
        </div>
      )}
    </div>
  );
}
